import React from 'react';

import { dashboardApi } from '../../services/api';
import type {
    HomeworkAlertDetail,
    HomeworkAlertStatus,
} from '../../types';
import { Button, LoadingState, Modal, SectionPanel, Select, StatusBadge, useToast } from '../ui';

interface HomeworkAlertDrawerProps {
    alertId: string | null;
    onClose: () => void;
    onChanged: () => void | Promise<void>;
}

const statusLabels: Record<HomeworkAlertStatus, string> = {
    OPEN: 'Cần xử lý',
    IN_PROGRESS: 'Đang xử lý',
    RESOLVED: 'Hoàn tất',
};

const statusTones: Record<HomeworkAlertStatus, 'danger' | 'warning' | 'success'> = {
    OPEN: 'danger',
    IN_PROGRESS: 'warning',
    RESOLVED: 'success',
};

const activityLabels = {
    CREATED: 'Tạo cảnh báo',
    STATUS_CHANGED: 'Đổi trạng thái',
    ASSIGNEE_CHANGED: 'Cập nhật theo dõi',
    NOTE_ADDED: 'Thêm ghi chú',
} as const;

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : 'Không thể cập nhật cảnh báo.';
}

function activityDetail(metadata: unknown) {
    if (!metadata || typeof metadata !== 'object') return '';
    const values = metadata as Record<string, unknown>;
    if (typeof values.oldStatus === 'string' && typeof values.newStatus === 'string') {
        return `${statusLabels[values.oldStatus as HomeworkAlertStatus] ?? values.oldStatus} → ${statusLabels[values.newStatus as HomeworkAlertStatus] ?? values.newStatus}`;
    }
    if (values.reason === 'HOMEWORK_RESULT_UPDATED' && Array.isArray(values.latestLessons)) {
        return values.latestLessons
            .map((lesson) => {
                if (!lesson || typeof lesson !== 'object') return null;
                const item = lesson as Record<string, unknown>;
                const lessonDate = typeof item.lessonDate === 'string'
                    ? new Date(item.lessonDate).toLocaleDateString('vi-VN')
                    : 'Không rõ ngày';
                const completed = Number(item.completedExercises);
                const assigned = Number(item.assignedExercises);
                const percent = Number(item.completionPercent);
                if (!Number.isFinite(completed) || !Number.isFinite(assigned)) return null;
                const roundedPercent = Number.isFinite(percent) ? `, ${Math.round(percent)}%` : '';
                return `${lessonDate}: ${completed}/${assigned} bài${roundedPercent}`;
            })
            .filter(Boolean)
            .join('\n');
    }
    return '';
}

function activityLabel(activity: Pick<HomeworkAlertDetail['activities'][number], 'type' | 'metadata'>) {
    if (
        activity.type === 'NOTE_ADDED'
        && activity.metadata
        && typeof activity.metadata === 'object'
        && (activity.metadata as Record<string, unknown>).reason === 'HOMEWORK_RESULT_UPDATED'
    ) {
        return 'Cập nhật kết quả bài tập';
    }
    return activityLabels[activity.type];
}

export default function HomeworkAlertDrawer({
    alertId,
    onClose,
    onChanged,
}: HomeworkAlertDrawerProps) {
    const { showToast } = useToast();
    const [detail, setDetail] = React.useState<HomeworkAlertDetail | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [statusLoading, setStatusLoading] = React.useState(false);
    const [noteLoading, setNoteLoading] = React.useState(false);
    const [note, setNote] = React.useState('');
    const [error, setError] = React.useState('');
    const alertIdRef = React.useRef(alertId);
    alertIdRef.current = alertId;

    const loadDetail = React.useCallback(async (expectedAlertId = alertId) => {
        if (!expectedAlertId) return;
        const response = await dashboardApi.getHomeworkAlert(expectedAlertId);
        if (alertIdRef.current === expectedAlertId) {
            setDetail(response.data.data);
        }
    }, [alertId]);

    React.useEffect(() => {
        if (!alertId) {
            setDetail(null);
            setNote('');
            setError('');
            return;
        }

        let active = true;
        setLoading(true);
        setError('');
        setStatusLoading(false);
        setNoteLoading(false);
        dashboardApi.getHomeworkAlert(alertId)
            .then((detailResponse) => {
                if (!active) return;
                setDetail(detailResponse.data.data);
            })
            .catch((loadError) => {
                if (active) setError(errorMessage(loadError));
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [alertId]);

    const refreshAfterMutation = async (
        updatedDetail: HomeworkAlertDetail,
        expectedAlertId: string,
    ) => {
        if (alertIdRef.current !== expectedAlertId) return;
        setDetail((current) => ({
            ...updatedDetail,
            activities: updatedDetail.activities.length
                ? updatedDetail.activities
                : current?.activities ?? [],
        }));
        await Promise.allSettled([
            loadDetail(expectedAlertId),
            Promise.resolve(onChanged()),
        ]);
    };

    const updateStatus = async (status: HomeworkAlertStatus) => {
        if (!alertId || status === detail?.status) return;
        setStatusLoading(true);
        setError('');
        const expectedAlertId = alertId;
        try {
            const response = await dashboardApi.updateHomeworkAlertStatus(expectedAlertId, status);
            await refreshAfterMutation(response.data.data, expectedAlertId);
            if (alertIdRef.current !== expectedAlertId) return;
            showToast('Đã cập nhật trạng thái cảnh báo.', 'success');
        } catch (updateError) {
            if (alertIdRef.current === expectedAlertId) {
                setError(errorMessage(updateError));
            }
        } finally {
            if (alertIdRef.current === expectedAlertId) {
                setStatusLoading(false);
            }
        }
    };

    const saveNote = async (event: React.FormEvent) => {
        event.preventDefault();
        const trimmedNote = note.trim();
        if (!alertId || !trimmedNote) return;
        setNoteLoading(true);
        setError('');
        const expectedAlertId = alertId;
        try {
            const response = await dashboardApi.addHomeworkAlertNote(expectedAlertId, trimmedNote);
            if (alertIdRef.current !== expectedAlertId) return;
            setNote('');
            await refreshAfterMutation(response.data.data, expectedAlertId);
            if (alertIdRef.current !== expectedAlertId) return;
            showToast('Đã lưu ghi chú theo dõi.', 'success');
        } catch (saveError) {
            if (alertIdRef.current === expectedAlertId) {
                setError(errorMessage(saveError));
            }
        } finally {
            if (alertIdRef.current === expectedAlertId) {
                setNoteLoading(false);
            }
        }
    };

    const studentName = detail
        ? `${detail.student.firstName} ${detail.student.lastName}`.trim()
        : 'Chi tiết cảnh báo';

    return (
        <Modal
            open={Boolean(alertId)}
            title={studentName}
            description={detail ? `${detail.class.name} · ${detail.class.campus.name}` : undefined}
            onClose={onClose}
            className="max-h-[90vh] max-w-5xl overflow-y-auto"
        >
            {loading && !detail ? <LoadingState title="Đang tải chi tiết cảnh báo..." /> : null}
            {error ? <p role="alert" className="mb-4 rounded-lg border border-danger-100 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p> : null}
            {detail ? (
                <div className="space-y-6">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {[detail.firstLesson, detail.secondLesson].map((lesson, index) => (
                            <SectionPanel key={lesson.attendanceId} className="p-4">
                                <p className="text-xs font-medium text-ink-500">
                                    Buổi {index + 1} · {new Date(lesson.lessonDate).toLocaleDateString('vi-VN')}
                                </p>
                                <p className="mt-2 text-2xl font-semibold text-danger-700">
                                    {Math.round(lesson.completionPercent)}%
                                </p>
                                <p className="mt-1 text-sm text-ink-600">
                                    {lesson.completedExercises}/{lesson.assignedExercises} bài đã hoàn thành
                                </p>
                            </SectionPanel>
                        ))}
                    </div>

                    <SectionPanel className="p-4 sm:p-5">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-ink-900">Theo dõi cảnh báo</h3>
                                <p className="mt-1 text-xs text-ink-500">Cập nhật trạng thái và ghi chú liên hệ.</p>
                            </div>
                            <StatusBadge tone={statusTones[detail.status]}>{statusLabels[detail.status]}</StatusBadge>
                        </div>
                        <Select
                            label="Trạng thái"
                            value={detail.status}
                            disabled={statusLoading}
                            onChange={(event) => void updateStatus(event.target.value as HomeworkAlertStatus)}
                        >
                            {Object.entries(statusLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </Select>
                    </SectionPanel>

                    {detail.student.parentPhone ? (
                        <p className="text-sm text-ink-600">
                            Số phụ huynh: <a className="font-medium text-academy-700 hover:text-academy-800" href={`tel:${detail.student.parentPhone}`}>{detail.student.parentPhone}</a>
                        </p>
                    ) : null}

                    <SectionPanel className="p-4 sm:p-5">
                        <form className="space-y-3" onSubmit={saveNote}>
                            <label htmlFor="homework-alert-note" className="block text-sm font-medium text-ink-700">
                                Ghi chú theo dõi
                            </label>
                            <textarea
                                id="homework-alert-note"
                                value={note}
                                maxLength={2000}
                                rows={4}
                                onChange={(event) => setNote(event.target.value)}
                                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 transition-colors duration-200 placeholder:text-ink-400 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 disabled:cursor-not-allowed disabled:bg-ink-50"
                            />
                            <Button type="submit" loading={noteLoading} disabled={!note.trim()}>
                                Lưu ghi chú
                            </Button>
                        </form>
                    </SectionPanel>

                    <SectionPanel className="p-4 sm:p-5">
                        <h3 className="text-sm font-semibold text-ink-900">Lịch sử theo dõi</h3>
                        <div className="mt-3 space-y-3">
                            {detail.activities.length ? detail.activities.map((activity) => (
                                <div key={activity.id} className="border-l-2 border-ink-200 pl-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-sm font-medium text-ink-800">{activityLabel(activity)}</p>
                                        <time className="text-xs text-ink-500">
                                            {new Date(activity.createdAt).toLocaleString('vi-VN')}
                                        </time>
                                    </div>
                                    <p className="mt-1 text-xs text-ink-500">
                                        {activity.actor ? `${activity.actor.firstName} ${activity.actor.lastName}` : 'Hệ thống'}
                                    </p>
                                    {activity.note ? <p className="mt-1 text-sm text-ink-700">{activity.note}</p> : null}
                                    {activityDetail(activity.metadata) ? (
                                        <p className="mt-1 whitespace-pre-line text-sm text-ink-700">{activityDetail(activity.metadata)}</p>
                                    ) : null}
                                </div>
                            )) : <p className="text-sm text-ink-500">Chưa có hoạt động theo dõi.</p>}
                        </div>
                    </SectionPanel>
                </div>
            ) : null}
        </Modal>
    );
}
